import { Observable, Subject } from 'rxjs';
import { FactoryProvider, InjectionToken } from '@angular/core';

/**
 * Represents a communication channel between {@link LazyBusyStateStreamLoader} and {@link LoadingStateService}.
 *
 * The lazy busy stream loader will use the subject injected using this token to tell the
 * loading service of changes to lazy busy streams, and the loading service will use it
 * to subscribe to those changes and add/remove them from the pipe.
 *
 * This is used instead of directly injecting the loader into the loading service so that:
 * 1. Basic abstraction and decoupling.
 * 2. If the app doesn't have any lazy busy signals, the lazy stream loader can be tree-shaked.
 */
export const LAZY_BUSY_STATE_STREAMS_CHANGED = new InjectionToken('__LAZY_BUSY_STATE_STREAMS_CHANGED__', {
    providedIn: 'root',
    factory: (): Subject<Observable<boolean>[]> => new Subject()
});

/**
 * A token used to provide busy states to the `LoadingStateService` for mechanisms doing heavy work.
 *
 * Prefer using {@link provideBusyStateStream} to provide busy states instead of specifying this token directly.
 */
export const BUSY_STATE_STREAMS = new InjectionToken('__BUSY_STATE_STREAMS__', { factory: (): Observable<boolean>[] => [] });

// `deps` has to be typed as `any` to allow factories to specify their arg types. Do not replace with `unknown`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type BusyStateStreamFactory = (...deps: any[]) => Observable<boolean>;

// This simply strong-types the config object received by `provideBusyStateStream()`.
interface BusyStateFactoryConfig extends Omit<FactoryProvider, 'provide' | 'multi'> {
    useFactory: BusyStateStreamFactory;
}

/**
 * A provider used to hook busy states of heavy lifting mechanisms into the `LoadingStateService`.
 *
 * Prefer using {@link provideBusyStateStream} to provide busy states instead of using this provider directly.
 *
 * @export
 * @interface BusyStateStreamFactoryProvider
 * @extends {FactoryProvider}
 */
export interface BusyStateStreamFactoryProvider extends FactoryProvider {
    useFactory: BusyStateStreamFactory;
}

/**
 * Creates a strongly-typed provider for busy states as it is expected by the `LoadingStateService`.
 *
 * @export
 * @param {BusyStateFactoryConfig} config The configuration for the factory creating the busy state stream.
 * @return {*}  {BusyStateStreamFactoryProvider}
 */
export function provideBusyStateStream(config: BusyStateFactoryConfig): BusyStateStreamFactoryProvider {
    return {
        ...config,
        provide: BUSY_STATE_STREAMS,
        multi: true
    } as const;
}
